import { MAX_NAME_PART_BYTES } from "odyssey-shared";
import type { PartyRequest } from "./members.ts";

/**
 * The party's chat commands (ADR-101 decisions 3, 4 and 9): `/invite First Family`, `/remove First Family`,
 * `/promote First Family` and `/grant First Family [on|off]`, each naming a full name, read on the page before
 * anything is sent. The gateway decides who may do what.
 */

/**
 * A part of a name as a command names it: the characters every name admits, at most the wire's 32.
 */
const NAME_PART = new RegExp(`^[A-Za-z0-9_-]{1,${MAX_NAME_PART_BYTES}}$`);

/**
 * A command's full name and what follows it.
 */
const NAMED = /^\s*(\S+)\s+(\S+)(?:\s+(\S+))?\s*$/;

/**
 * The request a party command's text makes.
 *
 * @param command The command's id: invite, remove, promote or grant.
 * @param rest What follows the command's word.
 * @returns The request, or null when the text is no full name (or, for `/grant`, names a state other than on or off, or
 *   any other command is given a third word).
 * @example
 * partyRequestOf("grant", "Mira Thornwood off");
 * // { kind: "partyGrant", firstName: "Mira", familyName: "Thornwood", mayInvite: false }
 */
export function partyRequestOf(command: "invite" | "remove" | "promote" | "grant", rest: string): PartyRequest | null {
  const parts = NAMED.exec(rest);
  const firstName = parts?.[1];
  const familyName = parts?.[2];
  const state = parts?.[3];
  if (firstName === undefined || familyName === undefined || !NAME_PART.test(firstName) || !NAME_PART.test(familyName)) {
    return null;
  }
  if (command === "grant") {
    const word = state?.toLowerCase();
    return word === undefined || word === "on" || word === "off" ? { kind: "partyGrant", firstName, familyName, mayInvite: word !== "off" } : null;
  }
  if (state !== undefined) {
    return null;
  }
  return { kind: command === "invite" ? "partyInvite" : command === "remove" ? "partyRemove" : "partyPromote", firstName, familyName };
}
